import React, { useState, useEffect, useCallback } from 'react';
import { UserPlus, Check, X, Loader2, Phone, MessageSquare, RefreshCw, Filter, Search, Eye, Truck, Hash, Calendar, User } from 'lucide-react';
import { apiService } from '../../services/api.service';
import { API_ENDPOINTS, FLEET_HUB_METHODS, FLEET_APP_EVENTS, emitFleetAppEvent } from '../../config/environment';
import { fleetToast } from '../../utils/toast';
import './JoinRequestsPage.css';

const STATUS = {
  PENDING: 1,
  APPROVED: 2,
  REJECTED: 3,
  CANCELLED: 4,
};

const JoinRequestStatusLabels = {
  [STATUS.PENDING]: 'Bekliyor',
  [STATUS.APPROVED]: 'Onaylandı',
  [STATUS.REJECTED]: 'Reddedildi',
  [STATUS.CANCELLED]: 'İptal Edildi',
};

const STATUS_FILTERS = [
  { value: null, label: 'Tümü' },
  { value: STATUS.PENDING, label: 'Bekleyen' },
  { value: STATUS.APPROVED, label: 'Onaylanan' },
  { value: STATUS.REJECTED, label: 'Reddedilen' },
];

const JoinRequestsPage = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [actionLoading, setActionLoading] = useState(null);
  const [rejectModal, setRejectModal] = useState(null);
  const [approveModal, setApproveModal] = useState(null);
  const [detailModal, setDetailModal] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [statusFilter, setStatusFilter] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const loadRequests = useCallback(async () => {
    try {
      setLoading(true);
      const body = { page, pageSize, filterModel: {} };

      if (statusFilter !== null) {
        body.filterModel.Status = { filter: String(statusFilter), type: 'eq' };
      }
      if (searchTerm) {
        body.filterModel.CourierName = { filter: searchTerm, type: 'contains' };
      }

      body.sortModels = [{ colId: 'RequestedAt', sort: 'desc' }];

      const response = await apiService.post(API_ENDPOINTS.FLEET_PANEL.JOIN_REQUESTS_LIST, body);

      if (response.success) {
        setRequests(response.data?.items || []);
        const total = response.data?.totalCount || 0;
        setTotalCount(total);
        setTotalPages(Math.max(1, Math.ceil(total / pageSize)));
      }
    } catch (error) {
      console.error('Failed to load join requests:', error);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, statusFilter, searchTerm]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  useEffect(() => {
    const handler = () => {
      loadRequests();
    };
    window.addEventListener(`fleet:${FLEET_HUB_METHODS.RECEIVE_JOIN_REQUEST_NOTIFICATION}`, handler);
    return () => window.removeEventListener(`fleet:${FLEET_HUB_METHODS.RECEIVE_JOIN_REQUEST_NOTIFICATION}`, handler);
  }, [loadRequests]);

  const handleApprove = async () => {
    if (!approveModal) return;
    try {
      setActionLoading(`approve-${approveModal.id}`);
      const response = await apiService.put(API_ENDPOINTS.FLEET_PANEL.JOIN_REQUEST_APPROVE(approveModal.id));
      if (response.success) {
        fleetToast.success('Talep onaylandı');
        setApproveModal(null);
        await loadRequests();
        // Sol menü rozeti + dashboard sayacı bu işlemden push almaz; kendimiz haber veriyoruz.
        emitFleetAppEvent(FLEET_APP_EVENTS.JOIN_REQUESTS_CHANGED);
      } else {
        fleetToast.error(response.error || 'Onaylama başarısız');
      }
    } catch (error) {
      fleetToast.error('Onaylama sırasında hata oluştu');
    } finally {
      setActionLoading(null);
    }
  };

  const openRejectModal = (request) => {
    setRejectModal(request);
    setRejectionReason('');
  };

  const handleReject = async () => {
    if (!rejectModal) return;
    try {
      setActionLoading(`reject-${rejectModal.id}`);
      const response = await apiService.put(
        API_ENDPOINTS.FLEET_PANEL.JOIN_REQUEST_REJECT(rejectModal.id),
        { rejectionReason: rejectionReason || undefined }
      );
      if (response.success) {
        fleetToast.success('Talep reddedildi');
        setRejectModal(null);
        setRejectionReason('');
        await loadRequests();
        emitFleetAppEvent(FLEET_APP_EVENTS.JOIN_REQUESTS_CHANGED);
      } else {
        fleetToast.error(response.error || 'Reddetme başarısız');
      }
    } catch (error) {
      fleetToast.error('Reddetme sırasında hata oluştu');
    } finally {
      setActionLoading(null);
    }
  };

  const handleFilterChange = (value) => {
    setStatusFilter(value);
    setPage(1);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
  };

  const getStatusBadge = (status) => {
    const label = JoinRequestStatusLabels[status] || status;
    const classMap = {
      [STATUS.PENDING]: 'fjr-badge-pending',
      [STATUS.APPROVED]: 'fjr-badge-approved',
      [STATUS.REJECTED]: 'fjr-badge-rejected',
      [STATUS.CANCELLED]: 'fjr-badge-rejected',
    };
    return <span className={`fjr-badge ${classMap[status] || ''}`}>{label}</span>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="fjr-container">
      <div className="page-header">
        <div className="header-left">
          <div className="page-icon"><UserPlus size={20} /></div>
          <div className="page-title">
            <h1>Katılım Talepleri</h1>
            <p>Filoya katılmak isteyen kuryeler {totalCount > 0 && `(${totalCount})`}</p>
          </div>
        </div>
        <button className="btn btn-secondary" onClick={loadRequests} disabled={loading}>
          <RefreshCw size={16} className={loading ? 'spinning' : ''} />
          Yenile
        </button>
      </div>

      <div className="page-toolbar">
        <form onSubmit={handleSearch} className="search-form">
          <div className="search-input-wrapper">
            <Search size={16} />
            <input
              type="text"
              placeholder="Kurye adı ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          <button type="submit" className="btn btn-primary">Ara</button>
        </form>
        <div className="fjr-filters">
          <Filter size={14} />
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.value ?? 'all'}
              className={`fjr-filter-btn ${statusFilter === f.value ? 'fjr-filter-active' : ''}`}
              onClick={() => handleFilterChange(f.value)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="page-loading">
          <Loader2 className="spinning" size={40} />
        </div>
      ) : requests.length === 0 ? (
        <div className="page-empty">
          <UserPlus size={48} />
          <h3>Henüz talep yok</h3>
          <p>Katılım talepleri burada görünecek.</p>
        </div>
      ) : (
        <>
          <div className="data-table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Kurye Adı</th>
                  <th>Telefon</th>
                  <th>Araç Türü</th>
                  <th>Mesaj</th>
                  <th>Tarih</th>
                  <th>Durum</th>
                  <th>İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {requests.map((request) => (
                  <tr
                    key={request.id}
                    className="fjr-row-clickable"
                    onClick={() => setDetailModal(request)}
                    title="Başvuru detayını görüntüle"
                  >
                    <td>
                      <div className="fjr-courier-info">
                        <div className="fjr-courier-name">{request.courierName || '-'}</div>
                        {request.courierAccountNumber && (
                          <div className="fjr-courier-account">{request.courierAccountNumber}</div>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="fjr-phone">
                        <Phone size={14} />
                        {request.courierPhone || '-'}
                      </div>
                    </td>
                    <td>{request.courierVehicleType || '-'}</td>
                    <td>
                      {request.message ? (
                        <div className="fjr-message" title={request.message}>
                          <MessageSquare size={14} />
                          {request.message.length > 30 ? request.message.slice(0, 30) + '...' : request.message}
                        </div>
                      ) : '-'}
                    </td>
                    <td>{formatDate(request.requestedAt)}</td>
                    <td>{getStatusBadge(request.status)}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      {request.status === STATUS.PENDING ? (
                        <div className="fjr-actions">
                          <button
                            className="fjr-action-btn fjr-action-detail"
                            onClick={() => setDetailModal(request)}
                            title="Detay"
                          >
                            <Eye size={18} />
                            <span>Detay</span>
                          </button>
                          <button
                            className="fjr-action-btn fjr-action-approve"
                            onClick={() => setApproveModal(request)}
                            disabled={!!actionLoading}
                            title="Onayla"
                          >
                            <Check size={18} />
                            <span>Onayla</span>
                          </button>
                          <button
                            className="fjr-action-btn fjr-action-reject"
                            onClick={() => openRejectModal(request)}
                            disabled={!!actionLoading}
                            title="Reddet"
                          >
                            <X size={18} />
                            <span>Reddet</span>
                          </button>
                        </div>
                      ) : (
                        /* Statü metni burada TEKRARLANMAZ — DURUM kolonundaki rozet zaten gösteriyor;
                           butonun yanına serbest metin konduğu için hizasız taşıyordu. */
                        <div className="fjr-actions">
                          <button
                            className="fjr-action-btn fjr-action-detail"
                            onClick={() => setDetailModal(request)}
                            title="Detay"
                          >
                            <Eye size={18} />
                            <span>Detay</span>
                          </button>
                          {request.rejectionReason && (
                            <span className="fjr-rejection-reason" title={request.rejectionReason}>
                              Red: {request.rejectionReason.length > 20 ? request.rejectionReason.slice(0, 20) + '...' : request.rejectionReason}
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="simple-pagination">
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Önceki
              </button>
              <span>
                Sayfa {page} / {totalPages}
              </span>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                Sonraki
              </button>
            </div>
          )}
        </>
      )}

      {detailModal && (
        <div className="modal-overlay" onClick={() => setDetailModal(null)}>
          <div className="modal fjr-detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Başvuru Detayı</h2>
              <button className="modal-close" onClick={() => setDetailModal(null)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="fjr-detail-grid">
                <div className="fjr-detail-row">
                  <span className="fjr-detail-label"><User size={15} /> Kurye</span>
                  <span className="fjr-detail-value">{detailModal.courierName || '-'}</span>
                </div>
                {detailModal.courierAccountNumber && (
                  <div className="fjr-detail-row">
                    <span className="fjr-detail-label"><Hash size={15} /> Hesap No</span>
                    <span className="fjr-detail-value">{detailModal.courierAccountNumber}</span>
                  </div>
                )}
                <div className="fjr-detail-row">
                  <span className="fjr-detail-label"><Phone size={15} /> Telefon</span>
                  <span className="fjr-detail-value">{detailModal.courierPhone || '-'}</span>
                </div>
                <div className="fjr-detail-row">
                  <span className="fjr-detail-label"><Truck size={15} /> Araç Türü</span>
                  <span className="fjr-detail-value">{detailModal.courierVehicleType || '-'}</span>
                </div>
                <div className="fjr-detail-row">
                  <span className="fjr-detail-label"><Calendar size={15} /> Başvuru Tarihi</span>
                  <span className="fjr-detail-value">{formatDate(detailModal.requestedAt)}</span>
                </div>
                {detailModal.respondedAt && (
                  <div className="fjr-detail-row">
                    <span className="fjr-detail-label"><Calendar size={15} /> Yanıt Tarihi</span>
                    <span className="fjr-detail-value">{formatDate(detailModal.respondedAt)}</span>
                  </div>
                )}
                <div className="fjr-detail-row">
                  <span className="fjr-detail-label"><Filter size={15} /> Durum</span>
                  <span className="fjr-detail-value">{getStatusBadge(detailModal.status)}</span>
                </div>
              </div>

              <div className="fjr-detail-message-block">
                <div className="fjr-detail-label"><MessageSquare size={15} /> Mesaj</div>
                <div className="fjr-detail-message">
                  {detailModal.message ? detailModal.message : <span className="fjr-detail-empty">Mesaj yok</span>}
                </div>
              </div>

              {detailModal.rejectionReason && (
                <div className="fjr-detail-message-block">
                  <div className="fjr-detail-label"><X size={15} /> Red Sebebi</div>
                  <div className="fjr-detail-message fjr-detail-rejection">{detailModal.rejectionReason}</div>
                </div>
              )}

              <div className="modal-actions">
                {detailModal.status === STATUS.PENDING && (
                  <>
                    <button
                      className="btn btn-danger"
                      onClick={() => { const r = detailModal; setDetailModal(null); openRejectModal(r); }}
                    >
                      <X size={16} /> Reddet
                    </button>
                    <button
                      className="btn btn-success"
                      onClick={() => { const r = detailModal; setDetailModal(null); setApproveModal(r); }}
                    >
                      <Check size={16} /> Onayla
                    </button>
                  </>
                )}
                <button className="btn btn-secondary" onClick={() => setDetailModal(null)}>
                  Kapat
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {approveModal && (
        <div className="modal-overlay" onClick={() => setApproveModal(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Talebi Onayla</h2>
              <button className="modal-close" onClick={() => setApproveModal(null)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <p className="fjr-modal-text">
                <strong>{approveModal.courierName}</strong> isimli kuryeyi filonuza eklemek istediğinize emin misiniz?
              </p>
              {approveModal.courierAccountNumber && (
                <p className="fjr-modal-detail">Hesap No: {approveModal.courierAccountNumber}</p>
              )}
              {approveModal.message && (
                <p className="fjr-modal-detail">Mesaj: {approveModal.message}</p>
              )}
              <div className="modal-actions">
                <button className="btn btn-secondary" onClick={() => setApproveModal(null)}>
                  Vazgeç
                </button>
                <button
                  className="btn btn-success"
                  onClick={handleApprove}
                  disabled={actionLoading?.startsWith('approve-')}
                >
                  {actionLoading?.startsWith('approve-') ? (
                    <Loader2 size={16} className="spinning" />
                  ) : (
                    <>
                      <Check size={16} />
                      Onayla
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {rejectModal && (
        <div className="modal-overlay" onClick={() => setRejectModal(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Talebi Reddet</h2>
              <button className="modal-close" onClick={() => setRejectModal(null)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <p className="fjr-modal-text">
                <strong>{rejectModal.courierName}</strong> isimli kuryenin talebini reddetmek istediğinize emin misiniz?
              </p>
              <label className="fjr-modal-label">Red Sebebi (opsiyonel)</label>
              <textarea
                className="fjr-modal-textarea"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Red sebebini yazınız..."
                rows={3}
              />
              <div className="modal-actions">
                <button className="btn btn-secondary" onClick={() => setRejectModal(null)}>
                  Vazgeç
                </button>
                <button
                  className="btn btn-danger"
                  onClick={handleReject}
                  disabled={actionLoading?.startsWith('reject-')}
                >
                  {actionLoading?.startsWith('reject-') ? (
                    <Loader2 size={16} className="spinning" />
                  ) : 'Reddet'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JoinRequestsPage;
